<template>
    <div
        class="fixed inset-0 w-full h-full overflow-hidden flex justify-center items-center"
        :class="modals.length > 0 ? 'bg-black bg-opacity-25' : 'pointer-events-none'"
        :style="{ zIndex: OVERLAY_Z_INDEX }"
        :data-testid="modals.length > 0 ? 'dynamic-modal-overlay' : undefined"
        :tabindex="modals.length > 0 ? -1 : undefined"
        ref="overlayRef"
        @click.self="onOverlayClick"
        @keydown="handleOverlayKeydown"
        v-if="modals.length > 0 || renderedDrawerList.length > 0"
    >
        <template :key="entry.id" v-for="(entry, index) in modals">
            <transition name="fade">
                <dynamic-modal-panel
                    :entry="entry"
                    :is-topmost="isTopmost(entry)"
                    :z-index="getEntryZIndex(index)"
                    :ref="(component) => setPanelRef(entry.id, component)"
                    @backdrop-click="onOverlayClick"
                    @dismiss="onClose(entry.id)"
                    @entry-close="onCloseWithCallback(entry)"
                    v-if="!isDrawer(entry)"
                />
            </transition>
        </template>

        <div
            class="contents"
            :data-testid="drawer.isActive ? undefined : 'leaving-drawer-layer'"
            :style="{ zIndex: OVERLAY_Z_INDEX }"
            :key="drawer.entry.id"
            v-for="drawer in renderedDrawerList"
        >
            <dynamic-drawer-panel
                :class="{ 'pointer-events-none': !drawer.isActive }"
                :entry="drawer.entry"
                :is-topmost="drawer.isActive && isTopmost(drawer.entry)"
                :transform="getRenderedDrawerTransform(drawer)"
                :z-index="drawer.zIndex"
                :ref="(component) => setPanelRef(drawer.entry.id, component)"
                @entry-close="onCloseWithCallback(drawer.entry)"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import {
        computed,
        nextTick,
        onBeforeUnmount,
        ref,
        watch,
        type ComponentPublicInstance,
    } from 'vue';
    import DynamicDrawerPanel from '@/components/dynamicModals/DynamicDrawerPanel.vue';
    import DynamicModalPanel from '@/components/dynamicModals/DynamicModalPanel.vue';
    import { type ModalData, useDynamicModalStore } from '@/store/modules/dynamicModals/store.ts';
    import useFocusTrap from '@/hooks/useFocusTrap.ts';
    import useBodyScrollLock from '@/hooks/useBodyScrollLock.ts';
    import useDrawerTransitions, {
        DRAWER_TRANSITION_DURATION_MS,
        getDrawerClosedTransform,
        getDrawerOpenTransform,
    } from '@/hooks/useDrawerTransitions.ts';
    import { DrawerSide, ModalMode } from '@/store/modules/dynamicModals/types.ts';

    const OVERLAY_Z_INDEX = 1000;

    type RenderedDrawer = {
        entry: ModalData;
        drawerSide: DrawerSide;
        isActive: boolean;
        isOpen: boolean;
        zIndex: string;
        frameIds: number[];
        timeoutId?: number;
    };

    const modals = computed<ModalData[]>(() => useDynamicModalStore().modals);
    const overlayRef = ref<HTMLElement | null>(null);
    const topmostPanelRef = ref<HTMLElement | null>(null);
    const panelRefs = new Map<string, HTMLElement>();
    const renderedDrawersById = ref<Record<string, RenderedDrawer>>({});

    const isDrawer = (entry: ModalData) => entry.mode === ModalMode.Drawer;
    const drawerSideOf = (entry: ModalData) => entry.drawerSide ?? DrawerSide.Right;
    const renderedDrawerList = computed(() => Object.values(renderedDrawersById.value));

    const drawerEntries = computed(() =>
        modals.value.filter(isDrawer).map((entry) => ({
            id: entry.id,
            drawerSide: drawerSideOf(entry),
        }))
    );

    const topmostEntry = computed<ModalData | undefined>(
        () => modals.value[modals.value.length - 1]
    );

    const { activate, focusFirst, release, handleKeydown } = useFocusTrap(topmostPanelRef);
    const { getDrawerTransform } = useDrawerTransitions(() => drawerEntries.value);

    useBodyScrollLock(computed(() => modals.value.length));

    const toHtmlElement = (value: Element | ComponentPublicInstance | null) => {
        if (value instanceof HTMLElement) {
            return value;
        }

        if (value?.$el instanceof HTMLElement) {
            return value.$el;
        }

        return null;
    };

    const syncTopmostPanelRef = () => {
        const entry = topmostEntry.value;
        topmostPanelRef.value = entry ? panelRefs.get(entry.id) ?? null : null;
    };

    const setPanelRef = (id: string, value: Element | ComponentPublicInstance | null) => {
        const element = toHtmlElement(value);
        if (element) {
            panelRefs.set(id, element);
        } else {
            panelRefs.delete(id);
        }

        if (topmostEntry.value?.id === id) {
            syncTopmostPanelRef();
        }
    };

    const isTopmost = (entry: ModalData) => topmostEntry.value?.id === entry.id;

    const getEntryZIndex = (index: number) => String(OVERLAY_Z_INDEX + 1 + index);

    const getRenderedDrawerTransform = (drawer: RenderedDrawer) => {
        if (drawer.isActive) {
            return getDrawerTransform(drawer.entry.id, drawer.drawerSide);
        }

        return drawer.isOpen
            ? getDrawerOpenTransform(drawer.drawerSide)
            : getDrawerClosedTransform(drawer.drawerSide);
    };

    const updateRenderedDrawer = (id: string, values: Partial<RenderedDrawer>) => {
        const drawer = renderedDrawersById.value[id];
        if (!drawer) {
            return;
        }

        renderedDrawersById.value = {
            ...renderedDrawersById.value,
            [id]: { ...drawer, ...values },
        };
    };

    const clearRenderedDrawerTimers = (drawer: RenderedDrawer) => {
        if (globalThis.window === undefined) {
            return;
        }

        drawer.frameIds.forEach((frameId) => globalThis.cancelAnimationFrame(frameId));
        if (drawer.timeoutId !== undefined) {
            globalThis.clearTimeout(drawer.timeoutId);
        }
    };

    const removeRenderedDrawer = (id: string) => {
        const drawer = renderedDrawersById.value[id];
        if (!drawer) {
            return;
        }

        clearRenderedDrawerTimers(drawer);
        const nextRenderedDrawersById = { ...renderedDrawersById.value };
        delete nextRenderedDrawersById[id];
        renderedDrawersById.value = nextRenderedDrawersById;
    };

    const scheduleLeavingDrawerSlideOut = (entryId: string) => {
        if (globalThis.window === undefined) {
            removeRenderedDrawer(entryId);
            return;
        }

        const frameIds: number[] = [];
        const firstFrameId = globalThis.requestAnimationFrame(() => {
            const secondFrameId = globalThis.requestAnimationFrame(() => {
                const timeoutId = globalThis.setTimeout(() => {
                    removeRenderedDrawer(entryId);
                }, DRAWER_TRANSITION_DURATION_MS);

                updateRenderedDrawer(entryId, {
                    isOpen: false,
                    timeoutId,
                });
            });

            frameIds.push(secondFrameId);
            updateRenderedDrawer(entryId, { frameIds });
        });

        frameIds.push(firstFrameId);
        updateRenderedDrawer(entryId, { frameIds });
    };

    const startLeavingDrawer = (entryId: string) => {
        const drawer = renderedDrawersById.value[entryId];
        if (!drawer || !drawer.isActive) {
            return;
        }

        updateRenderedDrawer(entryId, { isActive: false, isOpen: true });
        scheduleLeavingDrawerSlideOut(entryId);
    };

    // Animate every drawer that leaves the store, not only the ones dismissed
    // through the overlay/Escape/in-panel close paths. A programmatic close
    // (closeModal / closeModalsByName) removes the entry straight from the store,
    // so the slide-out has to be driven off that list to stay consistent.
    watch(
        () => modals.value,
        (currentModals) => {
            const currentDrawerIds = new Set<string>();
            const nextRenderedDrawersById = { ...renderedDrawersById.value };

            currentModals.forEach((entry, index) => {
                if (!isDrawer(entry)) {
                    return;
                }

                currentDrawerIds.add(entry.id);
                const existingDrawer = nextRenderedDrawersById[entry.id];
                nextRenderedDrawersById[entry.id] = {
                    entry,
                    drawerSide: drawerSideOf(entry),
                    isActive: true,
                    isOpen: existingDrawer?.isOpen ?? false,
                    zIndex: getEntryZIndex(index),
                    frameIds: existingDrawer?.frameIds ?? [],
                    timeoutId: existingDrawer?.timeoutId,
                };
            });

            renderedDrawersById.value = nextRenderedDrawersById;

            renderedDrawerList.value.forEach((drawer) => {
                if (drawer.isActive && !currentDrawerIds.has(drawer.entry.id)) {
                    startLeavingDrawer(drawer.entry.id);
                }
            });
        },
        { immediate: true }
    );

    onBeforeUnmount(() => {
        renderedDrawerList.value.forEach(clearRenderedDrawerTimers);
    });

    watch(
        () => topmostEntry.value?.id,
        async (entryId, previousEntryId) => {
            if (!entryId) {
                topmostPanelRef.value = null;
                release();
                return;
            }

            await nextTick();
            syncTopmostPanelRef();

            if (!previousEntryId) {
                activate();
                return;
            }

            focusFirst();
        },
        { immediate: true }
    );

    const onClose = (id: string) => {
        useDynamicModalStore().closeModal(id);
    };

    const onCloseWithCallback = (entry: ModalData) => {
        // The store watch drives the slide-out off the entry leaving `modals`,
        // so the close paths only need to remove it and fire the consumer callback.
        onClose(entry.id);
        entry.emits?.close?.();
    };

    const onOverlayClick = () => {
        const entry = topmostEntry.value;
        if (entry?.closeOnOverlayClick) {
            onCloseWithCallback(entry);
        }
    };

    const handleOverlayKeydown = (event: KeyboardEvent) => {
        handleKeydown(event);

        if (event.key !== 'Escape') {
            return;
        }

        const entry = topmostEntry.value;
        if (entry?.closeOnEscape) {
            onCloseWithCallback(entry);
        }
    };
</script>
